import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import api from "@/lib/api";
import { BLOG_CATEGORIES, IMAGES } from "@/lib/content";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get(`/blog${cat !== "All" ? `?category=${encodeURIComponent(cat)}` : ""}`).then((r) => setPosts(r.data));
  }, [cat]);

  return (
    <div className="relative z-10">
      <PageHeader
        overline="The Journal"
        titleLines={["Insights &", <span key="2" className="text-gold italic">Wisdom</span>]}
        subtitle="Explore articles on Scientific Vastu, astrology, numerology, energy healing and success stories."
        image={IMAGES.energy}
      />
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="blog-filters">
            {BLOG_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm transition-all duration-300 border ${cat === c ? "bg-primary-green text-white border-primary-green" : "border-white/15 text-[#94A3B8] hover:border-gold/40 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>
          {posts.length === 0 ? (
            <p className="text-[#94A3B8] py-20 text-center">No articles in this category yet.</p>
          ) : (
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {posts.map((p, i) => (
                  <motion.div layout key={p.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}>
                    <Link to={p.internal_link || `/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="group block overflow-hidden rounded-3xl border border-white/10 h-full transition-all duration-500 hover:border-gold/30 hover:-translate-y-1">
                      <div className="relative h-56 overflow-hidden">
                        <img src={p.cover_image} alt={p.image_alt || p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#060A17]/80 to-transparent" />
                        <span className="absolute top-4 left-4 rounded-full bg-primary-green/90 px-3 py-1 text-xs text-white">{p.category}</span>
                      </div>
                      <div className="p-6">
                        <h3 className="font-display text-2xl leading-tight group-hover:text-gold transition-colors">{p.title}</h3>
                        <p className="text-[#94A3B8] text-sm mt-3 line-clamp-2">{p.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-gold text-sm group-hover:gap-3 transition-all">Read article <ArrowRight size={15} /></span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>
      <CTABand />
    </div>
  );
}
